/* Uses the same cart event contract as Horizon's product-form.js (October 2026). */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class DentureWellCartUpsell extends HTMLElement {
  #busy = false;
  connectedCallback() { this.addEventListener('submit', this.#add); }
  disconnectedCallback() { this.removeEventListener('submit', this.#add); }

  #add = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const button = form.querySelector('button');
    const errorText = this.querySelector('.dw-cart-upsell__error');
    const statusText = this.querySelector('.dw-cart-upsell__status');
    this.#busy = true;
    button.disabled = true;
    button.textContent = 'Adding…';
    this.setAttribute('aria-busy', 'true');
    errorText.hidden = true;
    statusText.textContent = '';

    // Wait for Horizon quantity mutations before making another cart mutation.
    const CartItems = customElements.get('cart-items-component');
    await Promise.allSettled(CartItems?.pendingQuantityUpdates ?? []);

    const data = new FormData(form);
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId))].slice(0, 5);
    data.set('sections', sections.join(','));
    data.set('sections_url', location.pathname + location.search);
    const quantity = Number(data.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();
    // Avoid an unhandled rejection when no cart consumer is mounted.
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart',
      lines: [{ merchandiseId: String(data.get('id')), quantity }],
      promise: deferred.promise,
    }));

    let added = false;
    let retrySafe = false;
    try {
      const response = await fetch(form.action, {
        method: 'POST', headers: { Accept: 'application/json' }, body: data,
      });
      if (response.ok) added = true;
      const result = await response.json();
      if (!response.ok || result.status) {
        added = false;
        retrySafe = true;
        throw new Error(result.description || result.message || 'Could not add this item. Please try again.');
      }
      added = true;
      const cartURL = new URL(form.action);
      cartURL.pathname = cartURL.pathname.replace(/\/cart\/add(?:\.js)?\/?$/, '/cart.js');
      const cartResponse = await fetch(cartURL, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Item added, but the cart could not refresh. Reload the page before adding it again.');
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items, sections: result.sections, source: 'denturewell-cart-upsell',
          sourceId: 'dw-cart-upsell', productId: this.dataset.productId,
          itemCount: quantity, didError: false,
        },
      });
      button.textContent = 'Added';
      statusText.textContent = 'Item added to your cart.';
      // On hide-in-cart, Horizon removes this card while morphing the returned section.
      // If the card remains, restore its button after the render has had time to settle.
      setTimeout(() => { if (this.isConnected) this.#reset(button); }, 900);
    } catch (error) {
      deferred.reject(error);
      const message = added
        ? 'Item added, but the cart could not refresh. Reload the page before adding it again.'
        : retrySafe ? (error.message || 'Could not add this item. Please try again.')
          : 'Could not confirm whether this item was added. Reload the page before trying again.';
      errorText.textContent = message;
      errorText.hidden = false;
      this.dispatchEvent(new CartErrorEvent({ error: message, code: added ? 'SERVICE_UNAVAILABLE' : 'INVALID' }));
      if (retrySafe) this.#reset(button);
      else { button.textContent = added ? 'Added' : 'Reload'; this.removeAttribute('aria-busy'); }
    }
  };
  #reset(button) {
    this.#busy = false;
    button.disabled = false;
    button.textContent = 'Add';
    this.removeAttribute('aria-busy');
  }
}
if (!customElements.get('dw-cart-upsell')) customElements.define('dw-cart-upsell', DentureWellCartUpsell);

if (!window.__dwGemPagesCartBridge) {
  window.__dwGemPagesCartBridge = true;

  window.addEventListener('gp:add-to-cart-success', async (event) => {
    if (!document.querySelector('#cart-drawer')) return;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) throw new Error('Could not refresh the cart.');

      const cart = await response.json();
      const added = event.detail?.data;
      const items = added?.items || (added?.id ? [added] : []);

      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.variant_id || item.id),
          quantity: item.quantity,
        })),
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'denturewell-gempages',
            didError: false,
          },
        }),
      }));
    } catch (error) {
      console.error('DentureWell cart refresh:', error);
      document.dispatchEvent(new CartErrorEvent({
        error: error.message,
        code: 'SERVICE_UNAVAILABLE',
      }));
    }
  });
}